import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Divider,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardIcon from '@mui/icons-material/Keyboard';

interface KeyboardShortcut {
  keys: string[];
  description: string;
  category: 'Navigation' | 'Actions' | 'General';
}

const shortcuts: KeyboardShortcut[] = [
  {
    keys: ['Cmd/Ctrl', 'K'],
    description: 'Open search',
    category: 'General',
  },
  {
    keys: ['Cmd/Ctrl', 'B'],
    description: 'Toggle sidebar',
    category: 'Navigation',
  },
  {
    keys: ['Tab'],
    description: 'Navigate through interactive elements',
    category: 'Navigation',
  },
  {
    keys: ['Shift', 'Tab'],
    description: 'Navigate backwards',
    category: 'Navigation',
  },
  {
    keys: ['Esc'],
    description: 'Close dialogs and modals',
    category: 'Actions',
  },
  {
    keys: ['Enter'],
    description: 'Activate focused button or submit form',
    category: 'Actions',
  },
  {
    keys: ['Space'],
    description: 'Toggle checkboxes and buttons',
    category: 'Actions',
  },
  {
    keys: ['Arrow Keys'],
    description: 'Navigate within lists and dropdowns',
    category: 'Navigation',
  },
  {
    keys: ['?'],
    description: 'Show this keyboard shortcuts guide',
    category: 'General',
  },
];

/**
 * KeyboardShortcutsGuide - Interactive guide showing keyboard shortcuts
 *
 * Features:
 * - Triggered by pressing "?" key
 * - Categorized shortcuts
 * - Visual key representations
 * - Accessible with ARIA labels
 *
 * Usage:
 * <KeyboardShortcutsGuide />
 */
export const KeyboardShortcutsGuide: React.FC = () => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Show guide with "?" key (Shift + /)
      if (event.key === '?' && !event.ctrlKey && !event.metaKey) {
        event.preventDefault();
        setOpen(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleClose = () => setOpen(false);

  const renderKey = (key: string) => (
    <Box
      component="kbd"
      sx={{
        display: 'inline-block',
        padding: theme.spacing(0.5, 1),
        backgroundColor: theme.palette.grey[100],
        border: `1px solid ${theme.palette.grey[300]}`,
        borderRadius: theme.shape.borderRadius / 2,
        fontFamily: 'monospace',
        fontSize: '0.875rem',
        fontWeight: 600,
        boxShadow: `0 2px 0 ${theme.palette.grey[300]}`,
        marginRight: theme.spacing(0.5),
      }}
    >
      {key}
    </Box>
  );

  const groupedShortcuts = shortcuts.reduce((acc, shortcut) => {
    if (!acc[shortcut.category]) {
      acc[shortcut.category] = [];
    }
    acc[shortcut.category].push(shortcut);
    return acc;
  }, {} as Record<string, KeyboardShortcut[]>);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="keyboard-shortcuts-title"
    >
      <DialogTitle
        id="keyboard-shortcuts-title"
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <KeyboardIcon color="primary" />
          <Typography variant="h6" component="span">
            Keyboard Shortcuts
          </Typography>
        </Box>
        <IconButton
          aria-label="Close keyboard shortcuts guide"
          onClick={handleClose}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {Object.entries(groupedShortcuts).map(([category, categoryShortcuts], index) => (
          <Box key={category} sx={{ mb: index < Object.keys(groupedShortcuts).length - 1 ? 3 : 0 }}>
            <Typography
              variant="overline"
              sx={{
                color: theme.palette.text.secondary,
                fontWeight: 700,
                letterSpacing: '0.08em',
                display: 'block',
                mb: 1.5,
              }}
            >
              {category}
            </Typography>

            {categoryShortcuts.map((shortcut, idx) => (
              <React.Fragment key={idx}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 1.5,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.primary,
                      flex: 1,
                    }}
                  >
                    {shortcut.description}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {shortcut.keys.map((key, keyIdx) => (
                      <React.Fragment key={keyIdx}>
                        {renderKey(key)}
                        {keyIdx < shortcut.keys.length - 1 && (
                          <Typography
                            component="span"
                            sx={{ mx: 0.5, color: theme.palette.text.secondary }}
                          >
                            +
                          </Typography>
                        )}
                      </React.Fragment>
                    ))}
                  </Box>
                </Box>
                {idx < categoryShortcuts.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </Box>
        ))}

        <Box
          sx={{
            mt: 3,
            p: 2,
            backgroundColor: theme.palette.grey[50],
            borderRadius: theme.shape.borderRadius,
            borderLeft: `4px solid ${theme.palette.primary.main}`,
          }}
        >
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            <strong>Tip:</strong> Press <kbd>?</kbd> anytime to see this guide again.
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default KeyboardShortcutsGuide;
